import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import {
  getBuilderLocationPlanURL,
  getBuilderLotURL,
  getBuilderURL,
  getLotCommunityPlanByCommunityPlanIdURL,
} from "lib/endpointHelpers";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import { createLotCommunityPlanData } from "lib/utils/showroom/showroomData";
import { Helmet as Head } from "react-helmet-async";
import Loading from "ui/components/Loading";
import withAuthentication from "../../../../../../../../../components/withAuthentication";

function LotCommunityPlanShowroomPageReady() {
  const { builderId, locationId, locationPlanId, lotId } = useAppRouterParams();
  if (!builderId || !locationId || !locationPlanId || !lotId) {
    return <Loading />;
  }
  return <LotCommunityPlanShowroomPage {...{ builderId, locationId, locationPlanId, lotId }} />;
}

function LotCommunityPlanShowroomPage({
  builderId,
  locationId,
  locationPlanId,
  lotId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
  lotId: string;
}) {
  assertIsNotUndefined(builderId);

  const { data: lot, loading: loadingLot } = useFetchImmutable<ClientLotHydrated>(
    locationId && lotId ? getBuilderLotURL({ builderId, locationId, lotId }) : "",
    [builderId, locationId, lotId],
  );

  const { data: lotCommunityPlan, loading: loadingLotCommunityPlan } =
    useFetchImmutable<ClientLotCommunityPlanHydrated>(
      getLotCommunityPlanByCommunityPlanIdURL({ builderId, locationId, communityPlanId: locationPlanId, lotId }),

      [builderId, locationId, locationPlanId, lotId],
    );

  const { data: communityPlanBaseData, loading: loadingCommunityPlan } = useFetchImmutable<ClientLocationPlanBaseData>(
    getBuilderLocationPlanURL({ builderId, locationId, locationPlanId }),
    [builderId, locationId, locationPlanId],
  );

  const { data: builder, loading: loadingBuilder } = useFetchImmutable<ClientBuilder>(getBuilderURL(builderId), [
    builderId,
  ]);

  if (!loadingLot && !loadingLotCommunityPlan && !loadingCommunityPlan && !loadingBuilder) {
    const { masterPlan, locationPlan, locationHydrated, homePlanHydrated, elevationOverrides } =
      createLotCommunityPlanData(ensureDefined(communityPlanBaseData), lot, ensureDefined(lotCommunityPlan));
    const planName = lotCommunityPlan?.name || communityPlanBaseData?.locationPlan.name;
    return (
      <>
        <Head>
          <title>{getPageTitle(`Configure ${planName}`)}</title>
          <meta name="robots" content="noindex" />
        </Head>
        <Showroom
          builder={builder}
          masterPlan={masterPlan}
          location={locationHydrated}
          locationPlan={locationPlan}
          homePlan={homePlanHydrated}
          lot={lot}
          previewMode
          lotDiscounts={[]}
          homePlanDiscounts={[]}
          reportingUnitLots={communityPlanBaseData?.reportingUnitLots}
          elevationOverrides={elevationOverrides}
        />
      </>
    );
  }

  return <Loading />;
}

export const Route = createFileRoute(
  "/builders/$builder_id/locations/$location_id/location-plans/$location_plan_id/lots/$lot_id/showroom",
)({
  beforeLoad: ({ context, params }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    throw redirect({
      to: "/showroom",
      search: {
        builderId: params.builder_id,
        planType: "community_plans",
        planId: params.location_plan_id,
        lotId: params.lot_id,
      },
      replace: true,
    });
  },
  component: withAuthentication(LotCommunityPlanShowroomPageReady, {
    products: ["showroom"],
  }),
});
